import { Component, OnInit, Input, OnDestroy, AfterViewInit } from '@angular/core';
import { ClinicalEncounterService } from '@app/core/services/clinical-encounter.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { PatientsService, AuthService, UiStateService, ProviderService } from '@app/core/services';
import { FormBuilder } from '@angular/forms';
import {
  distinctUntilChanged,
  map,
  tap,
  take,
  switchMap,
  catchError,
  debounceTime,
  withLatestFrom,
  filter,
  delay,
  pairwise,
} from 'rxjs/operators';
import { AddFilesDialogData } from '@app/modules/sidebar/add-file-dialog/add-file-dialog.component';
import { AddFileUploadService } from '@app/core/services/add-file-upload.service';
import { from, combineLatest, of, Subscription } from 'rxjs';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { AppInsightTags, ApplicationInsightsService } from '@app/core/services/application-insights.service';
import { clinicalNoteEditorInit, clinicalNoteToHtml } from '@app/shared/functions/clinical-note';
import { MerakiClientService } from '@app/core/services/meraki-client.service';
import { DialogService } from '../nora-component/nora-services/nora-dialog.service';
import { AudioRecorderService } from '@app/modules/sidebar/speech-to-notes/services/audio-recorder.service';
import { switchTap } from '@app/core/functions/switch-tap';
import * as _ from 'lodash';
import { NoraRecorderService } from '@app/core/components/nora-recorder/nora-recorder.service';
import { SidebarLocations } from '@app/core/services/state/ui-state/ui-state.store';
import { loadingAction } from '@app/shared/functions/loading-action';

@Component({
  selector: 'shared-quick-notes',
  templateUrl: './quick-notes.component.html',
  styleUrls: ['./quick-notes.component.scss'],
})
export class QuickNotesComponent implements OnInit, OnDestroy, AfterViewInit {
  SidebarLocations = SidebarLocations;

  isSpeechToNotesDoneProcessing: boolean = false;
  useNewNora$ = this.providerService.UseNoraV2$;

  @Input('displayMode') displayMode: 'full' | 'sidebar';

  currentTenantId$ = this.authService.currentTenantId$;
  currentPatientId$ = this.uiStateService.currentPatientId$;
  noraRecorderVisible$ = this.uiStateService.noraRecorderVisible$;

  // Speech to Notes
  isResized: boolean;
  isRecording: boolean = false;
  isPaused: boolean = false;
  speechToNotesProcessingStatus: boolean = false;
  private subscriptions: Subscription = new Subscription();

  params$ = this.currentPatientId$.pipe(
    withLatestFrom(this.currentTenantId$),
    distinctUntilChanged(),
    // debounceTime(10),
    map(([patientId, tenantId]) => ({ tenantId, patientId })),
    untilDestroyed(this)
  );

  encounterInProgress$ = this.params$.pipe(
    switchMap(
      params => (params.patientId && this.clinicalEncounterService.encounterInProgress$(params.tenantId, params.patientId)) || of(null)
    )
  );

  sidebar$ = this.uiStateService.sidebar$.pipe(distinctUntilChanged(), untilDestroyed(this));

  readonly toggleSidebar = loadingAction(() =>
    this.sidebar$.pipe(
      take(1),
      tap(sidebar => {
        const sidebarLocation =
          sidebar !== SidebarLocations.NORA_CONSULT_SUMMARY ? SidebarLocations.NORA_CONSULT_SUMMARY : SidebarLocations.HIDDEN;

        this.uiStateService.navigateSidebar(sidebarLocation);
      })
    )
  );

  readonly stopRecording = loadingAction(() =>
    this.currentPatientId$.pipe(
      take(1),
      switchMap(currentPatientId => this.noraAudioService.recordingStatusFor(currentPatientId).pipe(take(1))),
      switchMap(status =>
        status !== 'idle' ? of(this.toggleSidebar.trigger()) : this.noraAudioService.confirmConcludeRecording$(this.dialog)
      )
    )
  );

  clinicalNoteForm = this.fb.group(
    {
      clinicalNote: '',
    },
    { updateOn: 'blur' }
  );

  editorConfig = {
    ...clinicalNoteEditorInit,
    placeholder: 'Capture general clinical notes',
    content_style: 'body{font-family:Roboto,\"Helvetica Neue\",sans-serif;font-size:14px;margin:8px}',
  };

  sidebarEditorConfig = {
    ...this.editorConfig,
    placeholder: 'Capture general clinical notes about the consultation here',
  };

  isMobile$ = this.uiStateService.isMobile$;

  speechToNotesEnabled$ = this.providerService.speechToNotesEnabled$;

  constructor(
    private clinicalEncounterService: ClinicalEncounterService,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private appInsightsService: ApplicationInsightsService,
    private authService: AuthService,
    private uiStateService: UiStateService,
    private merakiService: MerakiClientService,
    private providerService: ProviderService,
    private noraDialogService: DialogService,
    private noraAudioService: AudioRecorderService,
    private noraRecorderService: NoraRecorderService,
    private addFileUploadService: AddFileUploadService
  ) {}

  ngOnInit() {
    this.params$
      .pipe(
        take(1),
        switchMap(params => this.clinicalEncounterService.clinicalNoteById$(params.tenantId, params.patientId)),
        tap(clinicalNote => this.clinicalNoteForm.patchValue({ clinicalNote })),
        untilDestroyed(this)
      )
      .subscribe();

    this.providerService.UseNoraV2$.pipe(
      take(1),
      filter(useNewNora => useNewNora),
      withLatestFrom(this.uiStateService.noraRecorderVisible$),
      switchTap(([useNewNora, visible]) => {
        if (!visible) {
          return this.noraRecorderService.toggleRecorder$(this.dialog, this.currentPatientId$);
        }
        return of(null);
      })
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.setupClinicalFormWatchers();

    this.providerService.speechToNotesEnabled$
      .pipe(
        take(1),
        tap(speechToNotesEnabled => {
          if (speechToNotesEnabled) {
            this.getSpeechToNotes();
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  takePicture() {
    this.addFileUploadService
      .takePicture()
      .pipe(
        filter(file => !!file),
        tap(file => this.openAddFileDialog({ Files: [file] } as AddFilesDialogData)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  openAddFileDialog(dialogData: AddFilesDialogData = {} as AddFilesDialogData) {
    this.params$
      .pipe(
        take(1),
        switchMap(params => this.addFileUploadService.openAddFileDialog(params, dialogData)),
        untilDestroyed(this)
      )
      .subscribe();
  }

  setupClinicalFormWatchers() {
    // predefault value from encounter
    this.encounterInProgress$
      .pipe(
        take(1),
        map(e => e.ClinicalNote || ''),
        catchError(() => ''),
        // Legacy notes are plain text; the editor needs HTML. clinicalNoteToHtml is
        // idempotent, so notes already stored as HTML pass through.
        map(notes => clinicalNoteToHtml(notes)),
        tap(note => this.clinicalNoteForm.patchValue({ clinicalNote: note }, { emitEvent: false })),
        untilDestroyed(this)
      )
      .subscribe();

    // watch form
    this.clinicalNoteForm
      .get('clinicalNote')
      .valueChanges.pipe(
        withLatestFrom(this.params$, this.encounterInProgress$, this.speechToNotesEnabled$),
        filter(([form, params, encounter, speechEnabled]) => encounter?.EncounterId),
        distinctUntilChanged(),
        tap(([value, params, encounter]) => {
          this.clinicalEncounterService.updateClinicalNote(params.tenantId, params.patientId, value);

          this.appInsightsService.trackEvent(AppInsightTags.CONSULT_QUICK_NOTE, {
            EncounterId: encounter.EncounterId,
            text: value,
          });
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  getSpeechToNotes() {
    let initialEncounterId: string;

    this.encounterInProgress$
      .pipe(
        take(1),
        tap(encounter => (initialEncounterId = encounter?.EncounterId)),
        switchMap(encounter =>
          encounter?.EncounterId ? this.merakiService.listenForTranscription(encounter.EncounterId, encounter.PracticeId) : of(null)
        ),
        distinctUntilChanged(_.isEqual),
        debounceTime(250),
        withLatestFrom(this.params$, this.encounterInProgress$),
        tap(([transcription, params, currentEncounter]) => {
          // Only process if we have a transcription and it matches the initial encounter
          if (transcription) {
            this.isSpeechToNotesDoneProcessing = true;
            this.speechToNotesProcessingStatus = false;
            this.noraAudioService.setProcessingNotesStatus(false);

            if (currentEncounter?.EncounterId === initialEncounterId) {
              var currentValue = this.clinicalNoteForm.value.clinicalNote;
              if (currentValue !== transcription) {
                this.clinicalNoteForm.patchValue(
                  {
                    clinicalNote: transcription,
                  },
                  { emitEvent: false }
                );

                this.clinicalEncounterService.updateClinicalNote(params.tenantId, params.patientId, transcription);
              }
            }
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  updateSpeechToNotesDialogSize(newSize: boolean): void {
    this.noraDialogService.setResized(newSize);
  }

  speechToNotesListeners() {
    const isResized$ = this.noraDialogService.isResized$;
    const recording$ = this.noraAudioService.recording$;
    const paused$ = this.noraAudioService.paused$;
    const isSpeechToNotesProcessing$ = this.noraAudioService.isProcessingNotes$;

    this.subscriptions.add(
      combineLatest([isResized$, recording$, paused$, isSpeechToNotesProcessing$]) // combine all the observables
        .pipe(
          tap(([isResized, isRecording, isPaused, isSpeechToNotesProcessing]) => {
            this.isResized = isResized;
            this.isRecording = isRecording;
            this.isPaused = isPaused;
            this.speechToNotesProcessingStatus = isSpeechToNotesProcessing;
          }),
          untilDestroyed(this)
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    this.providerService.UseNoraV2$.pipe(
      take(1),
      filter(useNewNora => useNewNora),
      withLatestFrom(this.noraRecorderService.isRecording$, this.uiStateService.noraRecorderVisible$),
      tap(([useNewNora, isRecording, recorderVisible]) => {
        if (!isRecording && recorderVisible) {
          return this.uiStateService.toggleNoraRecorder();
        }
      })
    ).subscribe();
  }
}
